import { GlobalEvents } from '@highlight/ipc'
import { BEFORE_AUDIO_NOTE_SAVE_EVENT } from '@shared/audio-note-save'

type BeforeAudioNoteSaveHandler = () => Promise<void>

const handlers = new Set<BeforeAudioNoteSaveHandler>()
const writes = new Set<Promise<void>>()

export function registerBeforeAudioNoteSave(handler: BeforeAudioNoteSaveHandler): () => void {
  handlers.add(handler)
  return () => handlers.delete(handler)
}

export function trackBeforeAudioNoteSave(write: Promise<void>): Promise<void> {
  writes.add(write)
  write.then(
    () => writes.delete(write),
    () => writes.delete(write),
  )
  return write
}

/**
 * Settle everything owed before an audio note is written: run each handler,
 * then wait on the server writes they started.
 *
 * allSettled throughout — one handler that throws must neither cut short the
 * others nor reach the caller. The save this precedes matters more than the
 * flush, and the notes are on disk either way.
 */
export async function flushBeforeAudioNoteSave(): Promise<void> {
  await Promise.allSettled([...handlers].map((handler) => handler()))
  await Promise.allSettled([...writes])
}

/**
 * Answer main's pre-save flush request. Called once from the overlay entry, so
 * the handler is in place before `overlay.handlersReady` — the flag main gates
 * the request on — can flip.
 *
 * The overlay alone answers: it is the only renderer holding before-save
 * handlers, and a second responder would resolve main's invoke for work it
 * doesn't own.
 */
export function listenForBeforeAudioNoteSave(): void {
  if (window.__RENDERER_NAME !== 'overlay') {
    return
  }
  GlobalEvents.handle(BEFORE_AUDIO_NOTE_SAVE_EVENT, flushBeforeAudioNoteSave)
}
